import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SHARED_CONSTANTS, SIDE_NAVIGATION_RESET_PARAM, SIDE_NAVIGATION_RESET_PARAM_KEY } from '../../../../Shared/constants/commonConstants';
import { ApiError } from '../../../../Shared/interfaces/ApiError';
import useApiClient from '../../../../Shared/services/api';
import { useAppContext } from '../../../../Shared/store/appContext';
import { generateSearchSuggestions } from '../../../../Shared/utils/searchSuggestionUtils';
import { searchData } from '../../../../Shared/utils/searchUtils';
import { PAGE_SIZES } from '../../../../UiComponentsLibrary/CustomPagination/constants/PageSizes';
import { SearchSuggestion } from '../../../../UiComponentsLibrary/CustomSearchInput';
import { SnackbarVariants } from '../../../../UiComponentsLibrary/CustomSnackbar';
import { useBookingSheet } from '../../../services/helpers';
import { useInventoryCheck } from '../../../../InventoryDashboard/services/helpers';
import { SEARCHABLE_BOOKING_FIELDS } from '../../CreateBooking/constants/FormFieldsConstants';
import { createActionButtonConfig } from '../config/actionButtonConfig';
import { createButtonConfig } from '../config/buttonConfig';
import { QueueSheetManagementRows } from '../interface/QueueSheetInterface';
import { bookingActionsUtils } from '../utils/bookingActionsUtils';
import { bookingSearchAndPageChangeUtils } from '../utils/bookingSearchAndPageChangeUtils';
import { deleteBookingUtils } from '../utils/deleteBookingUtils';
import { restoreBookingUtils } from '../utils/restoreBookingUtils';
import { cancelBookingUtils } from '../utils/cancelBookingUtils';
import { useCancelBookingMutation } from './useCancelBookingMutation';
import { useDeleteBookingMutation } from './useDeleteBookingMutation';
import { useDownloadMutation } from './useDownloadMutation';
import { useRestoreBookingMutation } from './useRestoreBookingMutation';
import { BOOKING_STATUS } from '../../CreateBooking/constants/DetailedBookingConstants';
import { ART_EVENT_STATUS } from '../../../../EditArtEvent/constants/artEventStatus';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { URLS } from '../../../../../features/Shared/constants/url';

export const useQueueSheetManagement = () => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [currentSize, setCurrentSize] = useState<number>(PAGE_SIZES[0]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isCreateBookingModalOpen, setIsCreateBookingModalOpen] =
    useState<boolean>(false);
  const [bookingToDelete, setBookingToDelete] =
    useState<QueueSheetManagementRows | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState<boolean>(false);
  const [bookingToRestore, setBookingToRestore] =
    useState<QueueSheetManagementRows | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [bookingToCancel, setBookingToCancel] =
    useState<QueueSheetManagementRows | null>(null);

  const [isViewBooking, setIsViewBooking] = useState<boolean>(false);
  const [viewBookingId, setViewBookingId] = useState<string>('');

  const [editBookingId, setEditBookingId] = useState<string | null>(null);
  const [currentBookingStatus, setCurrentBookingStatus] = useState<
    string | undefined
  >(undefined);
  const [searchSuggestions, setSearchSuggestions] = useState<
    SearchSuggestion[]
  >([]);
  const [filteredBookingData, setFilteredBookingData] = useState<
    QueueSheetManagementRows[]
  >([]);

  const { t } = useTranslation();
  const api = useApiClient();
  const {
    state: { selectedArtEventId = '', selectedArtEventStatus, currentUser },
  } = useAppContext();
  const artEventId = selectedArtEventId ?? '';
  const userRole = currentUser?.role;
  const userPermissions = currentUser?.permissions;
  const isPrivilegedMode = currentUser?.privilegedMode ?? false;

  const bookingSheetParams = {
    sheetName: artEventId,
    ...(currentBookingStatus && { bookingStatus: currentBookingStatus }),
  };
  const {
    data: allBookingData,
    isLoading,
    error,
  } = useBookingSheet(api, bookingSheetParams);

  const { isError: isInventoryNotFound } = useInventoryCheck(api, artEventId);

  const isCreateBookingDisabled =
    !artEventId ||
    isInventoryNotFound ||
    (selectedArtEventStatus === ART_EVENT_STATUS.FROZEN && !isPrivilegedMode);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Reset all form states when reset URL parameter is present
  useEffect(() => {
    if (searchParams.get(SIDE_NAVIGATION_RESET_PARAM_KEY) === SIDE_NAVIGATION_RESET_PARAM.reset) {
      setIsViewBooking(false);
      setBookingToDelete(null);
      setBookingToRestore(null);
      setIsCreateBookingModalOpen(false);
      setCurrentBookingStatus(undefined);

      // Clean URL by removing reset parameter without triggering navigation
      navigate(URLS.BOOKINGS, { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    if (allBookingData && allBookingData.length > 0) {
      const suggestions = generateSearchSuggestions(
        allBookingData,
        SEARCHABLE_BOOKING_FIELDS
      );
      setSearchSuggestions(suggestions);
    }
  }, [allBookingData, artEventId, currentBookingStatus]);

  useEffect(() => {
    if (!allBookingData) {
      setFilteredBookingData([]);
      return;
    }
    if (!searchValue.trim()) {
      setFilteredBookingData(allBookingData);
      return;
    }
    const searchResults = searchData(
      allBookingData,
      searchValue,
      SEARCHABLE_BOOKING_FIELDS
    );
    const sortedResults = searchResults.sort((a, b) => b.entryId - a.entryId);
    setFilteredBookingData(sortedResults);
  }, [allBookingData, searchValue, artEventId, currentBookingStatus]);

  const downloadBookingMutation = useDownloadMutation(setSuccessMessage);

  const handleEditClick = (booking: QueueSheetManagementRows) => {
    setEditBookingId(booking.bookingId);
    setIsCreateBookingModalOpen(true);
  };
  const handleCloseSnackBar = () => {
    setSuccessMessage(null);
    downloadBookingMutation.reset();
  };

  const handleViewClick = (booking: QueueSheetManagementRows) => {
    setIsViewBooking(true);
    setViewBookingId(booking.bookingId);
  };

  const isSnackBarVisible = !!successMessage || !!downloadBookingMutation.error;
  const snackBarMessage = successMessage
    || t('download-error-message');
  const snackBarVariant = downloadBookingMutation.error
    ? SnackbarVariants.ERROR
    : SnackbarVariants.SUCCESS;

  const {
    totalItems,
    totalPages,
    bookingData,
    handlePageChange,
    handlePageSizeChange,
    handleSearchChange,
  } = bookingSearchAndPageChangeUtils(
    filteredBookingData,
    currentSize,
    currentPage,
    setCurrentPage,
    searchValue,
    setSearchValue,
    setCurrentSize
  );

  const {
    handleCreateBookingClick,
    handleActiveBookingsClick,
    handleCancelledBookingsClick,
    handleCloseCreateBookingModal,
    handleExportBooking,
  } = bookingActionsUtils(
    setIsCreateBookingModalOpen,
    setCurrentBookingStatus,
    setCurrentPage,
    setEditBookingId,
    artEventId,
    downloadBookingMutation,
    currentBookingStatus || ''
  );

  const deleteBookingMutation = useDeleteBookingMutation(
    setIsDeleteModalOpen,
    setBookingToDelete,
    setSuccessMessage,
    setCurrentPage
  );

  const restoreBookingMutation = useRestoreBookingMutation(
    setIsRestoreModalOpen,
    setBookingToRestore,
    setSuccessMessage,
    setCurrentPage
  );

  const cancelBookingMutation = useCancelBookingMutation(
    setIsCancelModalOpen,
    setBookingToCancel,
    setSuccessMessage,
    setCurrentPage
  );

  const { handleRestoreClick, handleRestoreCancel, handleRestoreConfirm } =
    restoreBookingUtils(
      setIsRestoreModalOpen,
      setBookingToRestore,
      bookingToRestore,
      restoreBookingMutation
    );

  const { handleDeleteClick, handleDeleteCancel, handleDeleteConfirm } =
    deleteBookingUtils(
      setIsDeleteModalOpen,
      setBookingToDelete,
      bookingToDelete,
      deleteBookingMutation
    );

  const {
    handleOpenCancelModal,
    handleCloseCancelModal,
    handleConfirmCancelBooking,
  } = cancelBookingUtils(
    setIsCancelModalOpen,
    setBookingToCancel,
    bookingToCancel,
    cancelBookingMutation
  );

  const isCancelledView = currentBookingStatus === BOOKING_STATUS.CANCELLED;

  const actionButtonConfig = useMemo(() => createActionButtonConfig(
    handleEditClick,
    handleDeleteClick,
    handleRestoreClick,
    handleOpenCancelModal,
    handleViewClick,
    isCancelledView
  ), [isCancelledView]);

  const uniqueBrands = useMemo(() => {
    if (!allBookingData) return [];
    return Array.from(
      new Set(allBookingData.map((booking) => booking.brand).filter(Boolean))
    ).sort();
  }, [allBookingData]);

  const uniqueSlotPackages = useMemo(() => {
    if (!allBookingData) return [];
    return Array.from(
      new Set(allBookingData.flatMap((booking) => booking.slotPackageNames ?? []).filter(Boolean))
    ).sort();
  }, [allBookingData]);

  const bookingSectionProps = {
    bookingError: error as ApiError,
    bookingData,
    isLoading,
    paginationProps: {
      currentPage,
      currentSize,
      totalPages,
      totalItems,
      handlePageChange,
      handlePageSizeChange,
    },
    handleSearchChange,
    searchValue,
    searchSuggestions,
    minCharactersForSuggestions:
      SHARED_CONSTANTS.MIN_CHARACTER_LIMIT_TO_TRIGGER_SEARCH,
    ...createButtonConfig({
      translate: t,
      handleCreateBookingClick,
      handleActiveBookingsClick,
      handleCancelledBookingsClick,
      isCreateBookingDisabled,
      artEventId,
      artEventStatus: selectedArtEventStatus ?? undefined,
      userPermissions,
      isPrivilegedMode,
    }),
    actionButtonConfig: actionButtonConfig,
    handleExportBooking,
    isDownloadDisabled:
      downloadBookingMutation.isPending ||
      searchValue.trim() !== '' ||
      !bookingData.length,
    brands: uniqueBrands,
    slotPackages: uniqueSlotPackages,
    isCancelledView,
  };

  return {
    viewBookingProps: {
      bookingId: viewBookingId,
      isViewBooking,
      setIsViewBooking,
    },
    bookingConfirmationModalProps: {
      isDeleteModalOpen,
      bookingToDelete,
      handleDeleteConfirm,
      handleDeleteCancel,
      deleteBookingMutation,
    },
    bookingRestoreModalProps: {
      isRestoreModalOpen,
      bookingToRestore,
      handleRestoreConfirm,
      handleRestoreCancel,
      restoreBookingMutation,
    },
    bookingCancelModalProps: {
      isCancelModalOpen,
      bookingToCancel,
      handleConfirmCancelBooking,
      handleCloseCancelModal,
      cancelBookingMutation,
    },
    createBookingModalProps: {
      isOpen: isCreateBookingModalOpen,
      onClose: handleCloseCreateBookingModal,
      editBookingId,
    },
    customSnackbarProps: {
      variant: snackBarVariant,
      message: snackBarMessage,
      isVisible: isSnackBarVisible,
      onClose: handleCloseSnackBar,
    },
    bookingSectionProps,
  };
};
